/**
 * MethodSteps — render Claude's method_summary as a numbered list with
 * subtle [timing] callouts when present.
 *
 * Input shape (per the updated RecipeParseSystem prompt):
 *   "1. Soak chickpeas overnight [8 hr].\n2. Drain and rinse.\n3. Boil [45 min]."
 *
 * Falls back gracefully to whitespace-preserved prose for older parses or
 * any response that didn't follow the numbered format.
 */
export function MethodSteps({ text }: { text: string }) {
  const steps = parseSteps(text)
  if (steps.length === 0) {
    // Not numbered — render as a prose block with newlines preserved.
    return (
      <p className="font-sans text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
        {text}
      </p>
    )
  }
  return (
    <ol className="space-y-3">
      {steps.map((s, i) => (
        <li
          key={i}
          className="flex items-baseline gap-3 sm:gap-4"
        >
          <span className="font-mono text-xs text-moss tabular-nums tracking-wider shrink-0 w-6">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="font-sans text-sm sm:text-base text-foreground/90 leading-relaxed">
            {renderStepBody(s.body, s.timing)}
          </span>
        </li>
      ))}
    </ol>
  )
}

type ParsedStep = { body: string; timing?: string }

const STEP_PREFIX = /^\s*(\d+)[\.\)]\s+/
// Pull the LAST [bracketed] segment as the "timing" call-out — typical
// outputs end with [8 hr] / [45 min] / [bake 350°F 25 min]. Anything else
// in brackets stays inline as part of the body.
const TIMING_TAIL = /\s*\[([^\]]+)\]\s*\.?\s*$/

function parseSteps(text: string): ParsedStep[] {
  if (!text) return []
  // Split on newlines first, then group lines that don't begin with a
  // number prefix into the previous step (handles soft-wrapped multi-line
  // steps).
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
  const out: ParsedStep[] = []
  for (const line of lines) {
    if (STEP_PREFIX.test(line)) {
      let body = line.replace(STEP_PREFIX, "").trim()
      let timing: string | undefined
      const m = body.match(TIMING_TAIL)
      if (m) {
        timing = m[1].trim()
        body = body.replace(TIMING_TAIL, "").trim()
      }
      out.push({ body, timing })
    } else if (out.length > 0) {
      out[out.length - 1].body += " " + line
    }
  }
  return out
}

function renderStepBody(body: string, timing?: string) {
  return (
    <>
      <span>{body}</span>
      {timing && (
        <span
          className="ml-2 inline-flex items-baseline gap-1 font-mono text-[10px] uppercase tracking-wider text-moss border border-moss/40 rounded px-1.5 py-0.5 whitespace-nowrap"
          title="timing"
        >
          {timing}
        </span>
      )}
    </>
  )
}
